import { useEffect, useRef, useState } from 'react';
import type { SceneItemView } from '@scoutaround/script-breakdown-shared';

import { ConversationEntry } from '../conversations/ConversationEntry.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { useSceneBreakdown } from './context.js';
import { ITEM_STATE_LABEL, ITEM_STATE_WORK, ORIGIN_LABEL, evidenceLabel } from './labels.js';

/**
 * ONE ITEM OF THE SCENE'S SHEET, as the producer decides on it.
 *
 * It says where it came from (read by AI, from a character cue, added by you), where it stands
 * IN THIS SCENE (awaiting your decision, on the sheet, not needed), and what is known about its
 * place in the text — and only then offers the verbs that apply from that state (the transition
 * table, `ITEM_TRANSITIONS`): Accept and Not needed while it awaits a decision, Not needed once it
 * is on the sheet, Accept and Restore once it is not needed. Nothing here reaches another scene.
 *
 * A verb is keyed by the reading's mention, so an item a person added by hand — which no reading
 * proposed here — is taken off with "Remove from this scene" instead.
 *
 * "Remove from every scene…" is the one way to reach other scenes, and it ASKS: offered for an item
 * on this scene's sheet that the server says is on more than one scene of the draft (`onScenes`),
 * it opens the scope dialog with exactly those scenes before anything is removed.
 */
export function ItemRow({ item, selected }: { item: SceneItemView; selected: boolean }) {
  const { projectId, selectItem, showItem, decide, rename, setCount, remove, removeEverywhere, busy, refusal } = useSceneBreakdown();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(item.name);
  const row = useRef<HTMLLIElement>(null);

  // The row picked in the script (or by a takeaway) is brought into view.
  useEffect(() => {
    if (!selected) return;
    const node = row.current;
    if (node !== null && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const locked = busy || refusal !== null;
  const canDecide = item.mentionId !== null;
  const located = item.evidence.ranges.length > 0;
  const renamable = item.productionElementId !== null && item.rowVersion !== null;
  // On the sheet here and on others of this draft: the server's list says which, and it is shown first.
  const elsewhere = item.state === 'ON_SHEET' && item.productionElementId !== null ? (item.onScenes?.length ?? 0) : 0;
  const key = item.key;

  const menu: MenuItem[] = [
    {
      id: 'rename',
      label: 'Rename',
      disabled: !renamable || locked,
      hint: renamable ? 'Changes the name everywhere it is on the sheet' : 'Accept it first: only an item on the sheet has a name to change',
      testId: `el-item-rename-${key}`,
      onSelect: () => {
        setName(item.name);
        setRenaming(true);
      },
    },
    ...(item.productionElementId !== null && item.state === 'ON_SHEET'
      ? [
          {
            id: 'remove',
            label: 'Remove from this scene',
            tone: 'danger' as const,
            disabled: locked,
            hint: 'Other scenes keep it',
            testId: `el-item-remove-${key}`,
            onSelect: () => {
              remove(item);
            },
          },
        ]
      : []),
    ...(elsewhere > 1
      ? [
          {
            id: 'remove-all',
            label: 'Remove from every scene…',
            tone: 'danger' as const,
            disabled: locked,
            hint: `It is on ${String(elsewhere)} scenes; you see which before anything is removed`,
            testId: `el-item-remove-all-${key}`,
            onSelect: () => {
              removeEverywhere(item);
            },
          },
        ]
      : []),
  ];

  return (
    <li
      ref={row}
      className="el-item"
      data-state={item.state}
      data-selected={selected ? '1' : '0'}
      data-testid={`el-item-${key}`}
    >
      <div className="el-item-head">
        {renaming ? (
          <form
            className="el-item-rename"
            data-testid={`el-item-rename-form-${key}`}
            onSubmit={(event) => {
              event.preventDefault();
              const next = name.trim();
              if (next === '' || next === item.name) {
                setRenaming(false);
                return;
              }
              rename(item, next);
              setRenaming(false);
            }}
          >
            <input
              value={name}
              aria-label={`New name for ${item.name}`}
              data-testid={`el-item-rename-input-${key}`}
              autoFocus
              maxLength={255}
              onChange={(event) => {
                setName(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setRenaming(false);
              }}
            />
            <button type="submit" className="ctl" data-testid={`el-item-rename-save-${key}`} disabled={name.trim() === ''}>
              Save
            </button>
            <button
              type="button"
              className="ctl"
              onClick={() => {
                setRenaming(false);
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            type="button"
            className="el-item-name"
            aria-pressed={selected}
            data-testid={`el-item-select-${key}`}
            onClick={() => {
              selectItem(selected ? null : key);
            }}
          >
            {item.name}
          </button>
        )}
        <StatusPill
          state={ITEM_STATE_WORK[item.state]}
          label={ITEM_STATE_LABEL[item.state]}
          size="sm"
          testId={`el-item-state-${key}`}
        />
      </div>

      <p className="el-item-meta">
        <span data-testid={`el-item-origin-${key}`}>{ORIGIN_LABEL[item.origin]}</span>
        <span aria-hidden="true">·</span>
        <span className="el-item-evidence" data-match={item.evidence.match} data-testid={`el-item-evidence-${key}`}>
          {evidenceLabel(item.evidence)}
        </span>
        {located ? (
          <button
            type="button"
            className="el-link"
            data-testid={`el-item-show-${key}`}
            onClick={() => {
              showItem(item);
            }}
          >
            Show in script
          </button>
        ) : null}
      </p>

      <div className="el-item-actions">
        {item.state === 'NOT_NEEDED' ? null : (
          <label className="el-count">
            <span className="sr-only">{`How many ${item.name} this scene needs`}</span>
            <input
              key={`${key}-${String(item.quantity ?? '')}`}
              type="number"
              min={1}
              max={9999}
              inputMode="numeric"
              data-testid={`el-item-count-${key}`}
              title={
                item.quantityHint !== null && item.quantity === null
                  ? `The script says ${String(item.quantityHint)} — type it to count it`
                  : 'How many this scene needs'
              }
              placeholder={item.quantityHint !== null ? String(item.quantityHint) : 'Qty'}
              defaultValue={item.quantity ?? ''}
              disabled={locked}
              onBlur={(event) => {
                const raw = event.target.value.trim();
                const next = raw === '' ? null : Number(raw);
                if (next !== null && (!Number.isInteger(next) || next < 1)) return;
                if (item.quantity === next) return;
                setCount(item, next);
              }}
            />
          </label>
        )}

        {canDecide && item.state !== 'ON_SHEET' ? (
          <button
            type="button"
            className="ctl el-verb"
            data-testid={`el-item-accept-${key}`}
            disabled={locked}
            title={refusal ?? undefined}
            onClick={() => {
              decide(item, 'ACCEPT');
            }}
          >
            Accept
          </button>
        ) : null}
        {canDecide && item.state !== 'NOT_NEEDED' ? (
          <button
            type="button"
            className="ctl el-verb"
            data-testid={`el-item-reject-${key}`}
            disabled={locked}
            title={refusal ?? undefined}
            onClick={() => {
              decide(item, 'REJECT');
            }}
          >
            Not needed
          </button>
        ) : null}
        {canDecide && item.state === 'NOT_NEEDED' ? (
          <button
            type="button"
            className="ctl el-verb"
            data-testid={`el-item-restore-${key}`}
            disabled={locked}
            title={refusal ?? undefined}
            onClick={() => {
              decide(item, 'RESTORE');
            }}
          >
            Restore
          </button>
        ) : null}

        <span className="el-item-end">
          {item.productionElementId !== null && item.state !== 'NOT_NEEDED' ? (
            <ConversationEntry projectId={projectId} anchorType="ELEMENT" anchorId={item.productionElementId} label={item.name} compact />
          ) : null}
          <MoreMenu items={menu} label={`More for ${item.name}`} testId={`el-item-more-${key}`} />
        </span>
      </div>
    </li>
  );
}
